import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import blogApi from "../../utils/api/blogApi";
import { BlogResponseDto } from "../../utils/types/blog";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { updateBlog } from "../../services/blogService";

export default function BlogEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [blog, setBlog] = useState<BlogResponseDto | null>(null);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [localization, setLocalization] = useState(0);
  const [isPublished, setIsPublished] = useState(false);

  const [newImage, setNewImage] = useState<File | null>(null);

  useEffect(() => {
    const loadBlog = async () => {
      try {
        const { data } = await blogApi.get<BlogResponseDto>(`/blogs/${id}`);
        console.log(data)
        setBlog(data);

        setTitle(data.title);
        setExcerpt(data.excerpt);
        setContent(data.content);
        setLocalization(data.localization);
        setIsPublished(data.isPublished);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadBlog();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("Title", title);
    formData.append("Excerpt", excerpt);
    formData.append("Content", content);
    formData.append("IsPublished", String(isPublished));
    formData.append("Localization", String(localization));

    if (newImage) {
      formData.append("mainImageFile", newImage);
    }

    try {
      await updateBlog(Number(id), formData);
      navigate("/admin/blogs");
    } catch (err) {
      console.error("Update failed", err);
      alert("Error updating blog");
    }
  };

  if (loading)
    return (
      <div className="text-center py-20 text-lg font-semibold text-gray-600">
        Loading blog...
      </div>
    );

  if (!blog)
    return (
      <div className="text-center py-20 text-lg font-semibold text-red-600">
        Blog not found.
      </div>
    );

  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <h1 className="text-4xl font-bold mb-8 text-center">Edit Blog</h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* Title */}
        <div>
          <label className="font-semibold text-gray-700 block mb-2">Title</label>
          <input
            type="text"
            className="w-full border p-3 rounded"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        {/* Excerpt */}
        <div>
          <label className="font-semibold text-gray-700 block mb-2">Excerpt</label>
          <textarea
            className="w-full border p-3 rounded"
            rows={3}
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            required
          />
        </div>

         {/* Current image */}
        <div>
          <label className="font-semibold text-gray-700 block mb-2">
            Current Image
          </label>

          {blog.mainImageUrl ? (
            <img
              src={blog.mainImageUrl}
              alt="Blog main"
              className="w-full max-h-64 object-cover rounded mb-4 shadow"
            />
          ) : (
            <p className="text-gray-500">No image uploaded.</p>
          )}
        </div>

        {/* Upload new image */}
        <div>
          <label className="font-semibold text-gray-700 block mb-2">
            Upload New Image (optional)
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setNewImage(e.target.files?.[0] ?? null)}
          />
        </div>

        {/* Content */}
        <div>
          <label className="font-semibold text-gray-700 block mb-2">Content</label>
          <ReactQuill value={content} onChange={setContent} />
        </div>

        {/* Localization */}
        <div>
          <label className="font-semibold text-gray-700 block mb-2">Localization</label>
          <select
            className="border p-3 rounded w-full"
            value={localization}
            onChange={(e) => setLocalization(Number(e.target.value))}
          >
            <option value={1}>MK</option>
            <option value={2}>RS</option>
            <option value={3}>EN</option>
          </select>
        </div>

        {/* Published */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={isPublished}
            onChange={(e) => setIsPublished(e.target.checked)}
          />
          <label className="font-semibold text-gray-700">Published</label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="bg-[#fa0053] text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition w-full"
        >
          Save Changes
        </button>
      </form>
    </div>
  );
}